import React from 'react';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { useLegalSettings } from '../hooks/useLegalSettings';

const LAST_UPDATED = 'December 2025';

export default function TermsPage() {
  const { business_name, abn, contact_email } = useLegalSettings();
  return (
    <div className="max-w-3xl mx-auto fade-in prose-legal" data-testid="terms-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
          <FileText className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-text">Terms of Service</h1>
          <p className="text-text-muted text-sm">Last updated: {LAST_UPDATED}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8 space-y-6 text-text leading-relaxed">
        <section>
          <h2 className="font-heading font-bold text-xl mb-2">1. About these Terms</h2>
          <p>
            These Terms of Service ("Terms") govern your use of the Digital Safety Course ("the Course") and the website operated by <strong>{business_name}</strong> (ABN <strong>{abn}</strong>), a sole trader based in Australia ("we", "us", "our"). By creating an account or purchasing the Course, you agree to be bound by these Terms. If you do not agree, please do not use the Course.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">2. Who can use the Course</h2>
          <p>
            You must be 18 years or older, or using the Course with the consent and supervision of a parent or guardian. You are responsible for keeping your account email address and password confidential.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">3. Purchase and payment</h2>
          <p>
            The Course is sold as a one-time purchase in Australian dollars. All payments are processed by Stripe. We do not store your card details on our servers. Access to paid modules is granted on confirmation of payment by Stripe.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">4. Your account and progress</h2>
          <p>
            When you create an account we store your first name, email address, and your course progress (checklist ticks, notes, quiz results and certificate) so that you can access the Course from any device. See our <Link to="/privacy" className="text-primary font-semibold hover:underline">Privacy Policy</Link> for details.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">5. Intellectual property</h2>
          <p>
            All content in the Course — written material, graphics, quizzes, worksheets, the BTA Buddy mascot and branding — is owned by <strong>{business_name}</strong> and protected by Australian and international copyright law. You may use it for your personal, non-commercial education. You may not copy, redistribute, resell or republish any part of the Course without our written permission.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">6. Acceptable use</h2>
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>share your account with other people</li>
            <li>attempt to circumvent paywalls, access controls or security measures</li>
            <li>use automated tools to scrape or copy content</li>
            <li>upload harmful code or attempt to disrupt the service</li>
          </ul>
          <p className="mt-2">We may suspend or terminate accounts that breach these rules.</p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">7. Course updates and availability</h2>
          <p>
            We may update, improve or replace Course content from time to time to keep it accurate and useful. We aim for high uptime but do not guarantee uninterrupted availability of the website.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">8. Educational purpose and disclaimer</h2>
          <p>
            The Course provides general digital safety education. It is not legal, financial or professional advice. We make reasonable efforts to keep the content accurate, but online threats evolve. You remain responsible for your own online decisions. To the extent permitted by law, our liability is limited to the amount you paid for the Course.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">9. Consumer rights</h2>
          <p>
            Our goods and services come with guarantees that cannot be excluded under the Australian Consumer Law. These Terms do not limit any rights or remedies you may have under that law. See our <Link to="/refund" className="text-primary font-semibold hover:underline">Refund Policy</Link>.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">10. Governing law</h2>
          <p>
            These Terms are governed by the laws of the State of Victoria, Australia, and the Commonwealth of Australia. You and we submit to the non-exclusive jurisdiction of the courts of Victoria.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">11. Changes to these Terms</h2>
          <p>
            We may update these Terms occasionally. The "last updated" date at the top of this page shows the most recent change. We'll notify you by email of material changes if you have an account with us.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">12. Contact</h2>
          <p>
            Questions about these Terms? Contact us at <a href={`mailto:${contact_email}`} className="text-primary font-semibold hover:underline">{contact_email}</a>.
          </p>
        </section>
      </div>
    </div>
  );
}
